import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Select from "react-select";
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage, functions } from "../firebase";
import { requireTenantBandContext, tenantStoragePath } from "../tenantContext";
import parseFilenameAsTitle from "../utils/parseFilenameAsTitle";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const keyOptions = [
  { value: "C",   label: "C (Major)"   },
  { value: "C#",  label: "C# (Major)"  },
  { value: "D",   label: "D (Major)"   },
  { value: "D#",  label: "D# (Major)"  },
  { value: "E",   label: "E (Major)"   },
  { value: "F",   label: "F (Major)"   },
  { value: "F#",  label: "F# (Major)"  },
  { value: "G",   label: "G (Major)"   },
  { value: "G#",  label: "G# (Major)"  },
  { value: "A",   label: "A (Major)"   },
  { value: "A#",  label: "A# (Major)"  },
  { value: "B",   label: "B (Major)"   },
  { value: "Cm",  label: "Cm (Minor)"  },
  { value: "C#m", label: "C#m (Minor)" },
  { value: "Dm",  label: "Dm (Minor)"  },
  { value: "D#m", label: "D#m (Minor)" },
  { value: "Em",  label: "Em (Minor)"  },
  { value: "Fm",  label: "Fm (Minor)"  },
  { value: "F#m", label: "F#m (Minor)" },
  { value: "Gm",  label: "Gm (Minor)"  },
  { value: "G#m", label: "G#m (Minor)" },
  { value: "Am",  label: "Am (Minor)"  },
  { value: "A#m", label: "A#m (Minor)" },
  { value: "Bm",  label: "Bm (Minor)"  },
];

function validateFiles(fileList) {
  const valid = [];
  Array.from(fileList).forEach((file) => {
    if (file.type === "application/pdf" && file.size <= MAX_FILE_SIZE) {
      valid.push(file);
    }
  });
  return valid;
}

function makeDraft(file, index) {
  return {
    id: `${Date.now()}-${index}-${file.name}`,
    file,
    title: parseFilenameAsTitle(file.name),
    artist: "",
    key: "",
    bpm: "",
    uploadStatus: "uploading",
    pdfUrl: "",
    pdfPath: "",
  };
}

// ── Status icons ──────────────────────────────────────────────────────────────

function SpinnerIcon() {
  return (
    <svg
      className="w-4 h-4 animate-spin text-muted shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="w-4 h-4 text-green-500 shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      className="w-4 h-4 text-red-500 shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  );
}

// ── Shared react-select styles for compact inline use ─────────────────────────

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    fontSize: "0.8125rem",
    borderColor: state.isFocused ? "var(--color-accent)" : "transparent",
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": { borderColor: "var(--color-accent)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, fontSize: "0.8125rem" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--color-accent)"
      : state.isFocused
      ? "var(--color-surface-hover)"
      : "transparent",
  }),
};

// ── Main component ─────────────────────────────────────────────────────────────

/**
 * Props:
 *   tenantContext       — resolved tenant (bandId, etc.)
 *   onSaved(song)       — called per successfully saved song
 *   currentSongCount    — current number of songs in the library
 *   songLimit           — free/paid tier song cap
 *   onLimitReached()    — called when save would exceed limit (caller decides what to show)
 *   onClose()           — called after a fully successful save (caller closes modal or advances step)
 *   mode                — "full" | "minimal" (default "full")
 *   autoTitleFromFilename — if true, re-apply filename-derived title on upload complete when title is empty
 *   onReadyCountChange(count, isSaving) — optional; fires whenever ready draft count or saving state changes
 *
 * Ref handle: { save() } — call to trigger the save flow from an external button.
 */
const BulkUploadForm = forwardRef(function BulkUploadForm(
  {
    tenantContext,
    onSaved,
    currentSongCount,
    songLimit,
    onLimitReached,
    onClose,
    mode = "full",
    autoTitleFromFilename = false,
    onReadyCountChange,
  },
  forwardedRef
) {
  const [isDragging, setIsDragging] = useState(false);
  const [songDrafts, setSongDrafts] = useState([]);
  const [bulkArtist, setBulkArtist] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const fileInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const songDraftsRef = useRef([]);

  useEffect(() => { songDraftsRef.current = songDrafts; }, [songDrafts]);

  // Auto-focus title when a single file finishes uploading
  useEffect(() => {
    if (songDrafts.length === 1 && songDrafts[0].uploadStatus === "done") {
      titleInputRef.current?.focus();
    }
  }, [songDrafts]);

  // Notify parent of ready count and saving state
  useEffect(() => {
    const readyCount = songDrafts.filter((d) => d.uploadStatus === "done").length;
    onReadyCountChange?.(readyCount, isSaving);
  }, [songDrafts, isSaving, onReadyCountChange]);

  const uploadFile = useCallback(
    async (draft, tenant) => {
      try {
        const storageRef = ref(
          storage,
          tenantStoragePath(tenant, "sheet_music", draft.file.name)
        );
        const snapshot = await uploadBytes(storageRef, draft.file);
        const pdfUrl = await getDownloadURL(snapshot.ref);
        setSongDrafts((prev) =>
          prev.map((d) =>
            d.id === draft.id
              ? {
                  ...d,
                  uploadStatus: "done",
                  pdfUrl,
                  pdfPath: snapshot.ref.fullPath,
                  title:
                    autoTitleFromFilename && d.title === ""
                      ? parseFilenameAsTitle(d.file.name)
                      : d.title,
                }
              : d
          )
        );
      } catch (err) {
        console.error("[BulkUploadForm] upload failed:", err);
        setSongDrafts((prev) =>
          prev.map((d) =>
            d.id === draft.id ? { ...d, uploadStatus: "error" } : d
          )
        );
      }
    },
    [autoTitleFromFilename]
  );

  const addFiles = useCallback(
    (fileList) => {
      const valid = validateFiles(fileList);
      if (!valid.length) return;

      const tenant = requireTenantBandContext(tenantContext);
      const current = songDraftsRef.current;
      const existingNames = new Set(current.map((d) => d.file.name));
      const newFiles = valid.filter((f) => !existingNames.has(f.name));
      if (!newFiles.length) return;

      const newDrafts = newFiles.map((file, i) => makeDraft(file, current.length + i));
      setSongDrafts((prev) => [...prev, ...newDrafts]);
      Promise.allSettled(newDrafts.map((draft) => uploadFile(draft, tenant)));
    },
    [tenantContext, uploadFile]
  );

  function patchDraft(id, patch) {
    setSongDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...patch } : d))
    );
  }

  function removeDraft(id) {
    setSongDrafts((prev) => {
      const draft = prev.find((d) => d.id === id);
      if (draft?.uploadStatus === "done" && draft.pdfPath) {
        deleteObject(ref(storage, draft.pdfPath)).catch(() => {});
      }
      return prev.filter((d) => d.id !== id);
    });
  }

  function applyBulkArtist() {
    const trimmed = bulkArtist.trim();
    if (!trimmed) return;
    setSongDrafts((prev) =>
      prev.map((d) => (d.artist === "" ? { ...d, artist: trimmed } : d))
    );
    setBulkArtist("");
  }

  const handleSave = useCallback(async () => {
    const readyDrafts = songDrafts.filter((d) => d.uploadStatus === "done");
    if (!readyDrafts.length) return;

    const slotsRemaining = Math.max(0, songLimit - currentSongCount);
    if (readyDrafts.length > slotsRemaining) {
      setSaveError(
        `You can only add ${slotsRemaining} more song${slotsRemaining === 1 ? "" : "s"} on your current plan. Remove some uploads or upgrade to continue.`
      );
      onLimitReached();
      return;
    }

    setIsSaving(true);
    setSaveError("");

    const tenant = requireTenantBandContext(tenantContext);
    const addSong = httpsCallable(functions, "addSong");

    try {
      const BATCH_SIZE = 5;
      const allOutcomes = [];
      for (let i = 0; i < readyDrafts.length; i += BATCH_SIZE) {
        const chunk = readyDrafts.slice(i, i + BATCH_SIZE);
        const chunkResults = await Promise.allSettled(
          chunk.map(async (draft) => {
            const payload = {
              bandId: tenant.bandId,
              title: draft.title.trim(),
              artist: draft.artist.trim(),
              key: draft.key,
              bpm: draft.bpm,
              pdfUrl: draft.pdfUrl,
              pdfPath: draft.pdfPath,
            };
            const result = await addSong(payload);
            return { result, payload };
          })
        );
        allOutcomes.push(...chunkResults);
      }

      const failed = [];
      allOutcomes.forEach((outcome, i) => {
        if (outcome.status === "fulfilled") {
          const { result, payload } = outcome.value;
          onSaved({ id: result.data.songId, ...payload });
        } else {
          failed.push(readyDrafts[i].title.trim() || `Song ${i + 1}`);
        }
      });

      if (failed.length > 0) {
        const allFailed = failed.length === readyDrafts.length;
        setSaveError(
          allFailed
            ? `Failed to save: ${failed.join(", ")}.`
            : `Failed to save: ${failed.join(", ")}. Others were added successfully.`
        );
      } else {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  }, [songDrafts, songLimit, currentSongCount, tenantContext, onSaved, onClose, onLimitReached]);

  useImperativeHandle(forwardedRef, () => ({ save: handleSave }), [handleSave]);

  function handleDragOver(e) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave() {
    setIsDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }

  function handleFileInputChange(e) {
    addFiles(e.target.files);
    e.target.value = "";
  }

  return (
    <div>
      {/* Drop zone */}
      <div
        className={[
          "min-h-40 flex flex-col items-center justify-center gap-2",
          "border-2 border-dashed rounded-lg cursor-pointer select-none",
          "transition-colors",
          isDragging
            ? "border-accent bg-accent/10"
            : "border-border hover:border-accent/60",
        ].join(" ")}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10 text-muted"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
          />
        </svg>
        <p className="font-medium text-sm">Drop your PDFs here</p>
        <p className="text-xs text-muted text-center px-4">
          or click to browse — you can select multiple at once
        </p>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        multiple
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Review table */}
      {songDrafts.length > 0 && (
        <div className="mt-4">
          {/* Bulk artist — full mode only */}
          {mode === "full" && (
            <div className="flex items-center gap-2 mb-3">
              <input
                type="text"
                value={bulkArtist}
                onChange={(e) => setBulkArtist(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyBulkArtist()}
                placeholder="Set artist for all rows…"
                className="flex-1 text-sm px-3 py-1.5 rounded-lg border border-border bg-transparent focus:outline-none focus:border-accent"
              />
              <button
                onClick={applyBulkArtist}
                className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-surface-hover"
              >
                Apply
              </button>
            </div>
          )}

          {/* Scrollable table */}
          <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted bg-surface-subtle sticky top-0">
                  <th className="py-2 px-2 text-center w-14">Status</th>
                  <th className="py-2 px-2 text-left min-w-0">Title</th>
                  {mode === "full" && <th className="py-2 px-2 text-left w-32">Artist</th>}
                  {mode === "full" && <th className="py-2 px-2 text-left w-36">Key</th>}
                  {mode === "full" && <th className="py-2 px-2 text-left w-24">BPM</th>}
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {songDrafts.map((draft, i) => (
                  <tr
                    key={draft.id}
                    className={[
                      "border-t border-border",
                      i % 2 === 0 ? "" : "bg-surface-subtle/50",
                    ].join(" ")}
                  >
                    {/* Status */}
                    <td className="py-2 px-2 text-center">
                      {draft.uploadStatus === "uploading" && <SpinnerIcon />}
                      {draft.uploadStatus === "done" && <CheckIcon />}
                      {draft.uploadStatus === "error" && <ErrorIcon />}
                    </td>

                    {/* Title */}
                    <td className="py-1 px-1">
                      <input
                        ref={songDrafts.length === 1 ? titleInputRef : null}
                        type="text"
                        value={draft.title}
                        onChange={(e) => patchDraft(draft.id, { title: e.target.value })}
                        disabled={isSaving}
                        className="w-full bg-transparent px-2 py-1 rounded focus:outline-none focus:border focus:border-accent border border-transparent text-sm disabled:opacity-50"
                      />
                    </td>

                    {/* Artist — full mode only */}
                    {mode === "full" && (
                      <td className="py-1 px-1">
                        <input
                          type="text"
                          value={draft.artist}
                          onChange={(e) => patchDraft(draft.id, { artist: e.target.value })}
                          placeholder="Optional"
                          disabled={isSaving}
                          className="w-full bg-transparent px-2 py-1 rounded focus:outline-none focus:border focus:border-accent border border-transparent text-sm placeholder:text-muted/50 disabled:opacity-50"
                        />
                      </td>
                    )}

                    {/* Key — full mode only */}
                    {mode === "full" && (
                      <td className="py-1 px-1">
                        <Select
                          options={keyOptions}
                          value={keyOptions.find((o) => o.value === draft.key) ?? null}
                          onChange={(opt) => patchDraft(draft.id, { key: opt?.value ?? "" })}
                          placeholder="—"
                          classNamePrefix="react-select"
                          isClearable
                          isDisabled={isSaving}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </td>
                    )}

                    {/* BPM — full mode only */}
                    {mode === "full" && (
                      <td className="py-1 px-1">
                        <input
                          type="number"
                          min={40}
                          max={300}
                          value={draft.bpm}
                          onChange={(e) => patchDraft(draft.id, { bpm: e.target.value })}
                          placeholder="Optional"
                          disabled={isSaving}
                          className="w-full bg-transparent px-2 py-1 rounded focus:outline-none focus:border focus:border-accent border border-transparent text-sm placeholder:text-muted/50 disabled:opacity-50"
                        />
                      </td>
                    )}

                    {/* Remove */}
                    <td className="py-1 px-2 text-center">
                      <button
                        onClick={() => removeDraft(draft.id)}
                        disabled={isSaving}
                        className="bg-transparent border-0 p-0 cursor-pointer text-danger hover:opacity-70 transition-opacity text-sm leading-none disabled:pointer-events-none disabled:opacity-30"
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Save error */}
      {saveError && (
        <p className="mt-3 text-sm text-red-500">{saveError}</p>
      )}
    </div>
  );
});

export default BulkUploadForm;
